/**
 * MobileReadinessAreasList — mobile view of the six governance-area
 * breakdown + evidence readiness list (Batch 7A).
 *
 * Server-authoritative: every score / weight / status label / regulatory
 * alignment / evidence ok flag / evidence note is rendered verbatim from
 * `readiness.breakdown[i]` and `readiness.evidence_readiness[i]`. No
 * client-side averaging, rounding or reinterpretation.
 */
import React from "react";
import { Link } from "react-router-dom";
import { CheckCircle2, AlertTriangle, ChevronRight } from "lucide-react";

const BAND_STYLES = {
  excellent:   { pill: "bg-emerald-100 text-emerald-800 border-emerald-300", bar: "bg-emerald-500" },
  strong:      { pill: "bg-teal-100 text-teal-800 border-teal-300",          bar: "bg-teal-500" },
  developing:  { pill: "bg-amber-100 text-amber-900 border-amber-300",       bar: "bg-amber-500" },
  improvement: { pill: "bg-orange-100 text-orange-900 border-orange-300",    bar: "bg-orange-500" },
  significant: { pill: "bg-red-100 text-red-800 border-red-300",             bar: "bg-red-600" },
};

function AreaCard({ b }) {
  const s = BAND_STYLES[b.status?.key] || BAND_STYLES.developing;
  return (
    <Link
      to={`/${b.module}`}
      className="block bg-white border border-slate-200 rounded-lg px-3 py-3 min-h-[44px] active:bg-slate-50"
      data-testid={`mobile-readiness-area-${b.module}`}
    >
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0 flex-1">
          <div className="text-sm font-semibold text-slate-900 break-words">{b.area}</div>
          <div className="text-[11px] text-slate-500 mt-0.5">Weight {b.weight}%</div>
        </div>
        <div className="text-right shrink-0">
          <div className="font-heading text-lg font-semibold tabular-nums text-slate-900" data-testid={`mobile-readiness-area-score-${b.module}`}>
            {b.score}%
          </div>
        </div>
      </div>
      <div className="h-1.5 bg-slate-100 rounded mt-2 w-full">
        <div className={`h-1.5 rounded ${s.bar}`} style={{ width: `${Math.max(2, Math.min(100, b.score))}%` }} />
      </div>
      <div className="mt-2 flex items-center justify-between gap-2">
        <span
          className={`inline-flex items-center px-2 py-0.5 rounded-full border text-[11px] font-semibold ${s.pill}`}
          data-testid={`mobile-readiness-area-status-${b.module}`}
        >
          {b.status?.label || "Developing"}
        </span>
        <ChevronRight className="w-4 h-4 text-slate-400" />
      </div>
      {b.regulatory_alignment && b.regulatory_alignment.length > 0 && (
        <div className="mt-2 flex flex-wrap gap-1" data-testid={`mobile-readiness-area-alignment-${b.module}`}>
          {b.regulatory_alignment.map((r) => (
            <span key={r} className="text-[10px] uppercase tracking-widest bg-slate-100 text-slate-700 border border-slate-200 px-1.5 py-0.5 rounded">
              {r}
            </span>
          ))}
        </div>
      )}
    </Link>
  );
}

export default function MobileReadinessAreasList({ breakdown, evidence_readiness }) {
  const list = Array.isArray(breakdown) ? breakdown : [];
  const evid = Array.isArray(evidence_readiness) ? evidence_readiness : [];
  return (
    <div className="space-y-4" data-testid="mobile-readiness-areas">
      <section>
        <div className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold mb-2 px-1">
          Governance Readiness Breakdown
        </div>
        <div className="text-[11px] text-slate-500 mb-2 px-1">
          Calculated from six key governance and operational readiness areas monitored within AYOBELL Care.
        </div>
        <div className="space-y-2">
          {list.map((b) => (
            <AreaCard key={b.area} b={b} />
          ))}
        </div>
        <div className="text-[10px] text-slate-400 mt-2 italic px-1">
          Regulatory Alignment maps each area to relevant CQC assessment themes. AYOBELL's internal weightings are not CQC-prescribed.
        </div>
      </section>

      {evid.length > 0 && (
        <section data-testid="mobile-readiness-evidence">
          <div className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold mb-2 px-1">
            Evidence Readiness
          </div>
          <div className="bg-white border border-slate-200 rounded-lg divide-y divide-slate-100">
            {evid.map((e) => (
              <Link
                key={e.area}
                to={`/${e.module}`}
                className="flex items-center justify-between gap-3 px-3 py-3 min-h-[44px] active:bg-slate-50"
                data-testid={`mobile-readiness-evidence-${e.module}`}
              >
                <div className="text-[13px] text-slate-800 break-words flex-1 min-w-0">{e.area}</div>
                <div className={`text-[11px] font-medium inline-flex items-center gap-1 shrink-0 ${e.ok ? "text-emerald-700" : "text-amber-700"}`}>
                  {e.ok ? <CheckCircle2 className="w-3.5 h-3.5" /> : <AlertTriangle className="w-3.5 h-3.5" />}
                  <span className="break-words text-right">{e.note}</span>
                </div>
              </Link>
            ))}
          </div>
        </section>
      )}
    </div>
  );
}
